// Model card (homepage and collection grids only).
//
// Swatches: the first tap on a color swatch previews that color on the card (image, price, VAT line,
// links); tapping the already-selected swatch follows its link to the product page.
//
// Random color: when a card first appears, it shows a random available color of its model.
// - Only swatches marked data-available="true" are eligible; with one eligible color it's shown as is.
// - The color shown last time for each model (sessionStorage, color handles only) is avoided when
//   another eligible color exists.
// - Moving between the homepage and a collection page via a link keeps the colors from the previous
//   page; a refresh or any other entry picks new ones.
// - A card never changes by itself after that: colors chosen in this page view (including manual
//   picks) are reused if the grid is re-rendered, for example after filtering.
//
// Loaded with defer after the grid; it runs once the page is parsed.
(() => {
  const STORAGE_KEY = 'ms-model-colors';
  const chosenThisView = new Map();

  const readStore = () => {
    try {
      return JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || '{}') || {};
    } catch {
      return {};
    }
  };

  const writeStore = (model, handle) => {
    try {
      const store = readStore();
      store[model] = handle;
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch {
      // Storage unavailable (private mode or blocked): colors are still random, repeats just aren't avoided.
    }
  };

  // True when this page was opened by a link from the homepage or a collection page on this store.
  const cameFromGrid = (() => {
    try {
      const nav = performance.getEntriesByType?.('navigation')?.[0];
      if (nav && nav.type !== 'navigate') return false;
      if (!document.referrer) return false;
      const ref = new URL(document.referrer);
      if (ref.origin !== window.location.origin) return false;
      const root = window.Shopify?.routes?.root || '/';
      const path = ref.pathname;
      return path === root || path === root.replace(/\/$/, '') || path.includes('/collections/');
    } catch {
      return false;
    }
  })();

  const storedAtLoad = readStore();

  class ModelCard extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.#onClick);
      this.#applyInitialColor();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.#onClick);
    }

    get #modelKey() {
      return this.dataset.modelKey || this.dataset.modelName || '';
    }

    #swatches() {
      return [...this.querySelectorAll('[data-model-swatch]')];
    }

    #applyInitialColor() {
      const model = this.#modelKey;
      const swatches = this.#swatches();
      if (!model || swatches.length < 2) return;

      const byHandle = (handle) => swatches.find((swatch) => swatch.dataset.handle === handle);
      const eligible = swatches.filter((swatch) => swatch.dataset.available === 'true');
      if (!eligible.length) return;

      // Same page view (grid re-rendered): keep whatever was showing, including manual picks.
      let target = byHandle(chosenThisView.get(model));

      // Linked from the other grid page: keep the colors the shopper just saw.
      if (!target && cameFromGrid) {
        const previous = byHandle(storedAtLoad[model]);
        if (previous && eligible.includes(previous)) target = previous;
      }

      if (!target) {
        const others = eligible.filter((swatch) => swatch.dataset.handle !== storedAtLoad[model]);
        const pool = others.length ? others : eligible;
        target = pool[Math.floor(Math.random() * pool.length)];
      }

      chosenThisView.set(model, target.dataset.handle);
      writeStore(model, target.dataset.handle);
      if (target.getAttribute('aria-current') === 'true') return;
      this.#selectWhenReady(target);
    }

    // First-load switch: keep the server-rendered color visible until the new photo is decoded
    // (max 2.5s), then swap photo, price and swatch together, so the card never shows an empty box.
    // A manual pick in the meantime wins.
    #selectWhenReady(swatch) {
      const panel = this.querySelector(`[data-model-panel="${swatch.dataset.index}"]`);
      const image = panel?.querySelector('img');
      if (!image) {
        this.#select(swatch);
        return;
      }
      image.loading = 'eager';
      const ready = image.complete ? Promise.resolve() : image.decode().catch(() => {});
      const timeout = new Promise((resolve) => window.setTimeout(resolve, 2500));
      Promise.race([ready, timeout]).then(() => {
        if (chosenThisView.get(this.#modelKey) !== swatch.dataset.handle) return;
        this.#select(swatch);
      });
    }

    #onClick = (event) => {
      const swatch = event.target.closest('[data-model-swatch]');
      if (!swatch || !this.contains(swatch)) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (swatch.getAttribute('aria-current') === 'true') return;

      event.preventDefault();
      this.#select(swatch);
      chosenThisView.set(this.#modelKey, swatch.dataset.handle);
      writeStore(this.#modelKey, swatch.dataset.handle);
    };

    #select(swatch) {
      const index = swatch.dataset.index;
      this.querySelectorAll('[data-model-panel]').forEach((panel) => {
        const active = panel.dataset.modelPanel === index;
        // Load the newly shown photo right away instead of waiting on lazy loading.
        if (active && panel.hidden) {
          const image = panel.querySelector('img');
          if (image) image.loading = 'eager';
        }
        panel.hidden = !active;
      });
      this.querySelectorAll('[data-model-price]').forEach((price) => {
        price.hidden = price.dataset.modelPrice !== index;
      });
      this.#swatches().forEach((item) => {
        item.setAttribute('aria-current', item === swatch ? 'true' : 'false');
      });
      const colorLabel = this.querySelector('[data-model-color]');
      if (colorLabel) colorLabel.textContent = swatch.dataset.color;
      const modelName = this.dataset.modelName || '';
      this.querySelectorAll('[data-model-link]').forEach((link) => {
        link.href = swatch.href;
        if (link.hasAttribute('aria-label')) link.setAttribute('aria-label', `${modelName}, ${swatch.dataset.color}`);
      });
    }
  }

  if (!customElements.get('s-model-card')) customElements.define('s-model-card', ModelCard);
})();
